import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {ApiUrl} from '@emm/core/util/api-url.token';
import {ChangeSet} from '@emm/core/models/change-set.model';
import {standardRetry} from '@emm/core/util/rx.util';
import {FeatureScope} from '@emm/core/util/feature-scope.constants';
import {MenuChange} from '@emm/core/models/menu-change.model';

@Injectable({
    providedIn: 'root',
})
export class ChangeSetService {
    constructor(@Inject(ApiUrl) private apiUrl: string, private http: HttpClient) {
    }

    private getBaseUrlV4(): string {
        return `${this.apiUrl}/change-sets/v4`;
    }


    public getAllFutureChangeSetsV4(): Observable<ChangeSet[]> {
        return this.http.get(`${this.getBaseUrlV4()}`).pipe(
            map((res) => res),
            standardRetry()
        );
    }

    public getFutureChangeSetsV4( // for full menu or submenu
        featureScope: string,
        consumerOrSubmenu: string,
        location: string,
        date: string
    ): Observable<ChangeSet[]> {
        let requestOptions = {};
        if (date) {
            if (location !== 'CHAIN') {
                requestOptions = {params: {effectiveDate: this.formatDate(date), location}};
            } else {
                requestOptions = {params: {effectiveDate: this.formatDate(date)}};
            }
        }
        let url = this.getBaseUrlV4();
        if (featureScope === FeatureScope.Viewer) { url = `${url}`; }
        switch (featureScope) {
            case FeatureScope.Viewer:
                return this.http
                    .get(`${url}/menu/${consumerOrSubmenu}/${location}/sets`, requestOptions)
                    .pipe(
                        map((res) => res),
                        standardRetry()
                    );
            case FeatureScope.Global:
            case FeatureScope.Client:
                return this.http
                    .get(`${url}/submenu/${consumerOrSubmenu}/sets`, requestOptions)
                    .pipe(
                        map((res) => res),
                        standardRetry()
                    );
        }
    }

    public getFutureChangeSetsForSubmenuV4( // for submenu
        submenu: string,
        location: string,
    ): Observable<ChangeSet[]> {
        let requestOptions = {};
        if (location !== 'CHAIN') {
            requestOptions = {params: {location}};
        }
        return this.http
            .get(`${this.apiUrl}/change-sets/v4/submenu/${submenu}/sets`, requestOptions)
            .pipe(
                map((res) => res),
                standardRetry()
            );
    }

    public rescheduleChangeSet(
        currentDate: string,
        newDate: string,
        dateChangedToToday: boolean,
    ): Observable<any> {
        let requestOptions = {};
        if (newDate) {
            const headers = {};
            if (dateChangedToToday) {
                headers['Apply-Change-Set-Now'] = String(dateChangedToToday);
            }
            requestOptions = {headers: new HttpHeaders(headers)};
        }
        const effectiveDate = this.formatDate(currentDate);
        const newEffectiveDate = this.formatDate(newDate);
        const request = {effectiveDate, newEffectiveDate};
        return this.http
            .put(`${this.getBaseUrlV4()}`, request)
            .pipe(standardRetry());
    }

    public getChangeSetChangesV4(
        featureScope: string,
        submenu: string,
        date: string,
        location: string,
        pageIndex: number,
        pageSize: number
    ): Observable<MenuChange[]> {
        let requestOptions = {};
        if (date) {
            if (location !== 'CHAIN') {
                requestOptions = {params: {effectiveDate: this.formatDate(date), location}};
            } else {
                requestOptions = {params: {effectiveDate: this.formatDate(date)}};
            }
        }
        const url = this.getBaseUrlV4();
        switch (featureScope) {
            case FeatureScope.Viewer:
                return this.http
                    .get(`${url}/menu/${submenu}/${location}/changes?page=${pageIndex}&size=${pageSize}`, requestOptions)
                    .pipe(
                        map((res) => res),
                        standardRetry()
                    );
            case FeatureScope.Global:
            case FeatureScope.Client:
                return this.http
                    .get(`${url}/submenu/${submenu}/changes?page=${pageIndex}&size=${pageSize}`, requestOptions)
                    .pipe(
                        map((res) => res),
                        standardRetry()
                    );
        }
    }

    public getChangeCountV4(
        featureScope: string,
        submenuOrConsumer: string,
        date: string,
        location: string
    ): Observable<number> {
        let requestOptions = {};
        if (date) {
            if (location !== 'CHAIN') {
                requestOptions = {params: {effectiveDate: this.formatDate(date), location}};
            } else {
                requestOptions = {params: {effectiveDate: this.formatDate(date)}};
            }
        }
        if (featureScope === 'Global' || featureScope === 'Client') {
            return this.http.get(`${this.getBaseUrlV4()}/submenu/${submenuOrConsumer}/changes/count`,
                requestOptions).pipe(
                map((res) => res['changeCount']),
                standardRetry()
            );
        } else {
            return this.http.get(`${this.getBaseUrlV4()}/menu/${submenuOrConsumer}/${location}/changes/count`,
                requestOptions).pipe(
                map((res) => res['changeCount']),
                standardRetry()
            );
        }
    }

    public getLastChangeV4(
        featureScope: string,
        submenuOrConsumer: string,
        date: string,
        location: string
    ): Observable<any> {
        let requestOptions = {};
        if (date) {
            if (location !== 'CHAIN') {
                requestOptions = {params: {effectiveDate: this.formatDate(date), location}};
            } else {
                requestOptions = {params: {effectiveDate: this.formatDate(date)}};
            }
        }
        if (featureScope === 'Global' || featureScope === 'Client') {
            return this.http
                .get(`${this.getBaseUrlV4()}/submenu/${submenuOrConsumer}/changes/last`, requestOptions)
                .pipe(standardRetry());
        } else {
            return this.http
                .get(`${this.getBaseUrlV4()}/menu/${submenuOrConsumer}/${location}/changes/last`, requestOptions)
                .pipe(standardRetry());
        }
    }

    public deleteLastChangeV4(
        featureScope: string,
        changeSetId: number,
        changeId: number
    ): Observable<any> {
        return this.http
            .delete(`${this.getBaseUrlV4()}/${changeSetId}/changes/${changeId}`)
            .pipe(standardRetry());
    }

    private formatDate(effectiveDate: string): string {
        // e.g. 2019-08-24T00:00:00.000
        const date = new Date(effectiveDate);
        date.setUTCHours(0, 0, 0, 0); // set time at midnight of selected day
        return date.toISOString().slice(0, -1); // remove the z at the end
    }
}
